import {acceptHMRUpdate, defineStore} from 'pinia';
import type {OrderBlock} from '@/types/orderblock';
import type {Product} from '@/types/product';
import type {Basket} from '@/types/basket';
import {dataLayerComposeProduct, dataLayerPush} from '@/utils/dataLayerHelper';
import type {NextStep} from '@/types/global-types';

const initialOrderBlocks: OrderBlock[] = window.composeOrderDecoratorJson?.orderBlocks ?? [];
const initialBasket: Basket = window.composeOrderDecoratorJson?.basket ?? null;
const areaId = window.composeOrderDecoratorJson?.areaId ?? null;
const orderProductBaseUrl = window.orderProductBaseUrl;
const removeProductBaseUrl = window.removeProductBaseUrl;
const nextStep = window.nextStep;

export const useStore = defineStore({
	id: 'dfnStore',
	state: () => ({
		orderBlocks: initialOrderBlocks,
		basket: initialBasket ?? null,
		areaId,
		orderProductBaseUrl,
		removeProductBaseUrl,
		loading: false,
		nextStep: {
			type: null,
			text: 'Ga verder',
			...nextStep
		} as NextStep
	}),
	actions: {
		orderAmount(product: Product, amount: number) {
			if ((product.mandatory && amount < 1) || product.unavailable || this.loading) {
				return;
			}

			const addProduct = amount > 0;
			let url = `${this.removeProductBaseUrl}&internalKey=${product.internalKey}`;

			if (addProduct) {
				url = `${this.orderProductBaseUrl}&internalKey=${product.internalKey}&amount=${amount}`;
			}

			this.executeOrderPostAndUpdate(url);
		},

		executeOrderPostAndUpdate(url: string) {
			// FIXME: better handling of ajax post and response
			this.loading = true;

			fetch(url, {method: 'POST'})
				.then((response) => {
					if (!response.ok) {
						throw Error;
					}
					return response;
				})
				.then(response => response.json())
				.then((data) => {
					this.orderBlocks = data.elements.decorator.orderBlocks;
					this.basket = data.elements.decorator.basket;
				})
				.catch((error) => {
					// FIXME: handle errors visually
					// eslint-disable-next-line no-alert
					alert('Something went wrong while updating the error. See console for more information');
					console.error(error);
				})
				.finally(() => {
					this.loading = false;
				});
		},

		toggleOrdered(product: Product) {
			if (product.mandatory || product.unavailable || this.loading) {
				return;
			}

			const removeProduct = product.amountOrdered > 0;
			const baseUrl = removeProduct ? this.removeProductBaseUrl : this.orderProductBaseUrl;
			const url = `${baseUrl}&internalKey=${product.internalKey}`;

			this.executeOrderPostAndUpdate(url);

			if (removeProduct) {
				this.dataLayerPushRemoveProduct(product);
			} else {
				this.dataLayerPushAddProduct(product);
			}
		},

		dataLayerPushAddProduct(product: Product) {
			dataLayerPush({
				event: 'addToCart',
				ecommerce: {
					'currencyCode': 'EUR',
					'add': {
						'products': [
							dataLayerComposeProduct(product)
						]
					}
				}
			});
		},

		dataLayerPushRemoveProduct(product: Product) {
			dataLayerPush({
				event: 'removeFromCart',
				ecommerce: {
					'currencyCode': 'EUR',
					'remove': {
						'products': [
							dataLayerComposeProduct(product)
						]
					}
				}
			});
		}

	}
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useStore, import.meta.hot));
}
